/**
 * ModelsListModal Component
 *
 * Modal for viewing and managing models of a platform provider.
 * Shows model health status, capabilities, and discovery metadata.
 */

import { AppModal } from '@/components/ui/app-modal';
import { useState } from 'react';
import { X, Plus, Trash2, Star, Loader2, Eye, EyeOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useI18n } from '@/i18n';
import {
  useProviderModels,
  useDeleteProviderModel,
  useUpdateProviderModel,
} from '../hooks/usePlatformProviders';
import { ModelFormModal } from './ModelFormModal';
import type { PlatformProvider, PlatformModel } from '../types';
import { getModelStatusStyle } from '../lib/statusStyles';

// =============================================================================
// Category Colors
// =============================================================================

const CATEGORY_COLORS: Record<string, string> = {
  main: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  router: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  tools: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  embedding: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
};

// =============================================================================
// Component
// =============================================================================

interface ModelsListModalProps {
  provider: PlatformProvider;
  onClose: () => void;
}

export function ModelsListModal({ provider, onClose }: ModelsListModalProps) {
  const { t } = useI18n();
  const [showAddModel, setShowAddModel] = useState(false);
  const [editingModel, setEditingModel] = useState<PlatformModel | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const { data: models, isLoading } = useProviderModels(provider.id, !showInactive);
  const deleteMutation = useDeleteProviderModel();
  const updateMutation = useUpdateProviderModel();

  const handleDeleteModel = async (modelId: string) => {
    if (confirm(t('common.confirmDelete'))) {
      await deleteMutation.mutateAsync({
        providerId: provider.id,
        modelId,
      });
    }
  };

  const handleToggleActive = async (model: PlatformModel) => {
    await updateMutation.mutateAsync({
      providerId: provider.id,
      modelId: model.id,
      data: { is_active: !model.is_active },
    });
  };

  const handleSetDefault = async (model: PlatformModel) => {
    await updateMutation.mutateAsync({
      providerId: provider.id,
      modelId: model.id,
      data: { is_default: true },
    });
  };

  const getCategoryColor = (category: string) => {
    return CATEGORY_COLORS[category] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  };

  const formatTokens = (tokens: number) => {
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(0)}K`;
    }
    return tokens.toString();
  };

  return (
    <AppModal
      onClose={onClose}
      ariaLabel={`${provider.display_name} — Models`}
      testId="models-list-modal"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {provider.display_name} — Models
            </h2>
            <p className="text-sm text-gray-500">{models?.length || 0} {t('admin.llmConfig.modelsModal.models')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowInactive(!showInactive)}
              title={showInactive ? 'Hide inactive' : 'Show inactive'}
            >
              {showInactive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              <span className="ml-1 hidden sm:inline">{showInactive ? t('admin.llmConfig.modelsModal.hideInactive') : t('admin.llmConfig.modelsModal.showAll')}</span>
            </Button>
            <Button size="sm" onClick={() => setShowAddModel(true)}>
              <Plus className="w-4 h-4 mr-1" />
              {t('admin.llmConfig.modelsModal.addModel')}
            </Button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : models?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>{t('admin.llmConfig.modelsModal.noModels')}</p>
              <Button onClick={() => setShowAddModel(true)} className="mt-4" size="sm">
                <Plus className="w-4 h-4 mr-1" />
                {t('admin.llmConfig.modelsModal.addFirstModel')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {models?.map((model) => {
                const statusInfo = getModelStatusStyle(model.model_status);
                const StatusIcon = statusInfo.icon;

                return (
                  <div
                    key={model.id}
                    className={cn(
                      'p-3 rounded-lg border',
                      model.is_active
                        ? 'border-gray-200 dark:border-gray-700'
                        : 'border-gray-300 dark:border-gray-600 opacity-60'
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Model status icon */}
                          <StatusIcon
                            className={cn('w-4 h-4 flex-shrink-0', statusInfo.className)}
                            aria-label={`Status: ${model.model_status}`}
                          />
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {model.display_name}
                          </span>
                          <span
                            className={cn(
                              'px-2 py-0.5 text-xs font-medium rounded-full',
                              getCategoryColor(model.category)
                            )}
                          >
                            {model.category}
                          </span>
                          {model.is_default && (
                            <span className="flex items-center gap-1 text-xs text-yellow-600 dark:text-yellow-400">
                              <Star className="w-3 h-3 fill-current" />
                              {t('admin.llmConfig.modelsModal.default')}
                            </span>
                          )}
                          {!model.is_active && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                              {t('admin.llmConfig.modelsModal.inactive')}
                            </span>
                          )}
                          {model.lifecycle_status && model.lifecycle_status !== 'Generally Available' && (
                            <span className="text-xs text-gray-400">{model.lifecycle_status}</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1 truncate">
                          {model.model_id}
                          {model.publisher && (
                            <span className="ml-2 text-gray-400">by {model.publisher}</span>
                          )}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                          <span>Context: {formatTokens(model.context_window)}</span>
                          <span>Max Output: {formatTokens(model.max_output_tokens)}</span>
                          {model.supports_chat && (
                            <span className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                              {t('admin.llmConfig.capabilities.chat')}
                            </span>
                          )}
                          {model.supports_embeddings && (
                            <span className="px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                              {t('admin.llmConfig.capabilities.embeddings')}
                            </span>
                          )}
                          {model.supports_vision && (
                            <span className="px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                              {t('admin.llmConfig.capabilities.vision')}
                            </span>
                          )}
                          {model.supports_tools && (
                            <span className="px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                              {t('admin.llmConfig.capabilities.tools')}
                            </span>
                          )}
                        </div>
                        {/* Health error */}
                        {model.last_health_error && (
                          <p className="mt-1 text-xs text-red-500 truncate" title={model.last_health_error}>
                            {model.last_health_error}
                          </p>
                        )}
                        {/* Deprecation warning */}
                        {model.deprecation_date && (
                          <p className="mt-1 text-xs text-yellow-600">
                            Deprecation: {new Date(model.deprecation_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0">
                        {/* Set default action */}
                        {!model.is_default && model.is_active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSetDefault(model)}
                            disabled={updateMutation.isPending}
                            title="Set as default for its category"
                          >
                            <Star className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Toggle active */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(model)}
                          disabled={updateMutation.isPending}
                          title={model.is_active ? 'Deactivate model' : 'Activate model'}
                        >
                          {model.is_active ? (
                            <EyeOff className="w-4 h-4 text-gray-500" />
                          ) : (
                            <Eye className="w-4 h-4 text-green-500" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingModel(model)}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteModel(model.id)}
                          disabled={deleteMutation.isPending}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
                        >
                          {deleteMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Add Model Modal */}
      {showAddModel && (
        <ModelFormModal
          providerId={provider.id}
          onClose={() => setShowAddModel(false)}
        />
      )}

      {/* Edit Model Modal */}
      {editingModel && (
        <ModelFormModal
          providerId={provider.id}
          model={editingModel}
          onClose={() => setEditingModel(null)}
        />
      )}
    </AppModal>
  );
}
